import { AlertCircle, CreditCard, X } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useState, useEffect, useRef } from "react";

export function PaymentFailureBanner() {
  const { currentOrganization } = useOrganization();
  const [dismissed, setDismissed] = useState(false);
  const prevHasPaymentFailureRef = useRef(false);

  // Show banner when payment has failed (past_due status)
  const hasPaymentFailure =
    currentOrganization?.accessPosture === "restricted" &&
    currentOrganization?.lifecycleSubstatus?.includes("past_due");

  // ✅ P0 FIX: Reset dismissal only when transitioning from no-failure → failure
  // This ensures banner reappears on NEW payment failures but stays dismissed for current one
  useEffect(() => {
    const prevHasPaymentFailure = prevHasPaymentFailureRef.current;
    
    // Only reset dismissal when transitioning from false → true (new failure event)
    if (!prevHasPaymentFailure && hasPaymentFailure) {
      setDismissed(false);
    }
    
    // Update ref for next render
    prevHasPaymentFailureRef.current = hasPaymentFailure;
  }, [hasPaymentFailure]);

  if (!currentOrganization) return null;
  if (!hasPaymentFailure) return null;
  if (dismissed) return null;

  // Calculate urgency level based on substatus
  const isHighUrgency = currentOrganization.lifecycleSubstatus?.includes("dunning_3") ||
                        currentOrganization.lifecycleSubstatus?.includes("dunning_final");
  
  const urgencyColor = isHighUrgency ? "bg-red-600" : "bg-orange-600";
  const urgencyText = isHighUrgency 
    ? "⚠️ Final Warning: Your account will be suspended soon" 
    : "⚠️ Payment Failed";

  return (
    <div
      className={`${urgencyColor} text-white px-4 py-3 sticky top-0 z-50 shadow-lg`}
      data-testid="payment-failure-banner"
    >
      <div className="container mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-sm">{urgencyText}</p>
            <p className="text-sm opacity-90">
              We couldn't process your payment. Update your payment method to restore access to all features.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link href="/settings/billing">
            <Button
              variant="secondary"
              size="sm"
              className="bg-white text-gray-900 hover:bg-gray-100"
              data-testid="button-update-payment"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Update Payment Method
            </Button>
          </Link>
          
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss banner"
            data-testid="button-dismiss-banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
